import { translate, useLanguage } from "./language";
/**
 * Generic UI primitives shared by every SPA.
 *
 * These live here rather than in @cqt/brand because they are behaviour (focus
 * traps, loading and error states), not design tokens — and because both the
 * admin and CRM apps need them, so duplicating them would let them drift.
 */
import { useEffect, useRef, useId, type ReactNode } from "react";

/**
 * Modal with a focus trap and Escape-to-close.
 *
 * Rolled by hand rather than pulling in Radix: Radix's scroll-lock renders real
 * <style> elements at runtime, which is what forces `style-src 'unsafe-inline'`
 * into the CSP. Avoiding it here keeps the policy tighter for the price of ~30
 * lines.
 */
export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useLanguage();
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  // Callers commonly pass an inline function, which is a new value on every
  // render. With [onClose] as the effect dependency, each keystroke re-ran the
  // effect and moved focus to the first control (the × button), so typing in
  // the invite form kept jumping out of the field. Read the latest onClose from
  // a ref and run the focus/trap setup once, when the dialog opens.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // Focus the first control so a keyboard user is inside the dialog, not
    // still on the page behind it.
    // Prefer a field over the header's × button, which comes first in the DOM.
    (panel.current?.querySelector<HTMLElement>("input, select, textarea")
      ?? panel.current?.querySelector<HTMLElement>("button"))?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !panel.current) return;

      const focusable = [
        ...panel.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ];
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      previouslyFocused?.focus();
    };
  }, []);

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panel}
      >
        <div className="card__head">
          <h3 id={titleId}>{typeof title === "string" ? translate(title) : title}</h3>
          <button className="btn btn--ghost btn--sm btn--icon" aria-label={translate("Close")} onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="card__body">{children}</div>
        {footer ? <div className="card__foot" style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>{footer}</div> : null}
      </div>
    </>
  );
}

export function Field({
  label,
  hint,
  children,
  htmlFor,
}: {
  label: ReactNode;
  hint?: string;
  children: ReactNode;
  htmlFor?: string;
}) {
  useLanguage();
  return (
    <div className="field" style={{ marginBottom: 14 }}>
      <label className="label" htmlFor={htmlFor}>
        {typeof label === "string" ? translate(label) : label}
      </label>
      {children}
      {hint ? <span className="hint">{translate(hint)}</span> : null}
    </div>
  );
}

export function Loading({ label = "Loading" }: { label?: string }) {
  useLanguage();
  return (
    <div className="row" style={{ padding: 32, justifyContent: "center", gap: 10 }}>
      <div className="spinner" aria-hidden="true" />
      <span className="meta">{typeof label === "string" ? translate(label) : label}</span>
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  useLanguage();
  const message =
    error instanceof Error ? error.message : typeof error === "string" ? error : "Unknown error";
  return (
    <div className="card">
      <div className="card__body">
        <div className="row" style={{ gap: 10, alignItems: "flex-start" }}>
          <span className="chip chip--danger">{translate("Error")}</span>
          <span className="error-text">{translate(message)}</span>
        </div>
      </div>
    </div>
  );
}
